import { Link } from "react-router";

import { ArrowUpRight } from "@/components/ui/Button";
import { useFooterNav, useSettings } from "@/content-context";
import { Logo } from "./Logo";

/**
 * Footer.
 *
 * Deep ground, wide leading, one hairline rule. The registration numbers and
 * partner references that used to sit in a bar above the header live here now,
 * so the credibility is still one scroll away without a strip competing with
 * the navigation on every page.
 *
 * Four columns, and the fourth is the one the client asked for. An institutional
 * reader deciding whether an organisation is real looks for the same short
 * list every time: who runs it, what it is registered as, what it has published,
 * what its policies say, how to reach somebody. Those used to be scattered
 * between About and Transparency. They are together now, under "Organisation".
 *
 * Columns, labels, links and their order all come from the CMS, with the
 * committed default behind them. Contact details come from the settings
 * document and appear only when they are confirmed, which is why this footer
 * now prints an address, a phone number and an email that it previously could
 * not: HISCUS publishes all three on its own website.
 */
export function Footer() {
  const year = new Date().getFullYear();
  const settings = useSettings();
  const footerNav = useFooterNav();

  return (
    <footer className="bg-midnight text-white">
      <div className="container-page py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-20">
          <div>
            <Logo tone="light" />

            <p className="mt-6 max-w-sm text-white/60" style={{ fontSize: "var(--step-lead)" }}>
              {settings.positioning}
            </p>

            <p className="mt-6 font-serif text-[1.375rem] text-sky">{settings.motto}</p>

            <address className="mt-8 space-y-1 text-[0.9375rem] not-italic text-white/70">
              {settings.address.confirmed && settings.address.value ? (
                <p>{settings.address.value}</p>
              ) : null}
              {settings.phone.confirmed && settings.phone.value ? (
                <p>
                  <a href={`tel:${settings.phone.value.replace(/\s/g, "")}`} className="link-underline">
                    {settings.phone.value}
                  </a>
                </p>
              ) : null}
              {settings.email.confirmed && settings.email.value ? (
                <p>
                  <a href={`mailto:${settings.email.value}`} className="link-underline">
                    {settings.email.value}
                  </a>
                </p>
              ) : (
                <p className="text-white/55">
                  <Link to="/contact" className="link-underline text-white/70">
                    Send an enquiry
                  </Link>{" "}
                  or reach HISCUS on the channels below.
                </p>
              )}
            </address>

            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-1">
              {settings.socialLinks.map((social) => (
                <li key={social.href}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline inline-flex min-h-11 items-center text-[0.875rem] text-white/70 hover:text-white"
                  >
                    {social.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-10 sm:grid-cols-2 xl:grid-cols-4">
            {footerNav.map((group) => (
              <nav key={group.heading} aria-label={group.heading}>
                <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-sky/70">
                  {group.heading}
                </h2>
                <ul className="mt-4 space-y-0.5">
                  {group.links.map((link) =>
                    link.external || /^https?:\/\//i.test(link.href) ? (
                      <li key={link.href + link.label}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-underline inline-flex min-h-10 items-center gap-1.5 text-[0.875rem] leading-snug text-white/70 hover:text-white"
                        >
                          {link.label}
                          <ArrowUpRight className="h-3 w-3" />
                          <span className="sr-only"> (opens in a new tab)</span>
                        </a>
                      </li>
                    ) : (
                      <li key={link.href + link.label}>
                        <Link
                          to={link.href}
                          className="link-underline inline-flex min-h-10 items-center text-[0.875rem] leading-snug text-white/70 hover:text-white"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ),
                  )}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <ul className="flex flex-wrap gap-x-10 gap-y-2 text-[0.8125rem] text-white/55">
            {settings.registrations.map((reg) => (
              <li key={reg.value}>
                {reg.label} <span className="text-white/75">{reg.value}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-[0.8125rem] text-white/55">
            <p>
              © {year} {settings.legalName}
            </p>
            <p>
              Website by{" "}
              <a
                href="https://avernek.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="group/credit inline-flex min-h-6 items-center gap-1 text-white/75 transition-colors duration-300 hover:text-white"
              >
                Avernek Technologies
                <ArrowUpRight className="transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/credit:-translate-y-0.5 group-hover/credit:translate-x-0.5" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
